'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var cx = require('classnames');
var reactI18next = require('react-i18next');
var AlloyButton = require('../alloy-button/AlloyButton.js');
var Input = require('./Input.js');
var ErrorMessage = require('./ErrorMessage.js');
var alloyInput_module = require('./alloy-input.module.scss.js');
var useDefaultInternalState = require('../../hooks/useDefaultInternalState.js');
var Validator = require('./Validator.js');
var MinusLightIcon = require('../alloy-icon/MinusLightIcon.js');
var PlusIcon = require('../alloy-icon/PlusIcon.js');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var cx__default = /*#__PURE__*/_interopDefault(cx);

var MAX_VALUE = '99999';
var MIN_VALUE = '0';
var QuantityInput = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var _h = _a.allowInputOutOfBounds, allowInputOutOfBounds = _h === void 0 ? false : _h, _j = _a.className, className = _j === void 0 ? '' : _j, _k = _a.decIcon, DecIcon = _k === void 0 ? MinusLightIcon.default : _k, decrement = _a.decrement, disabled = _a.disabled, _l = _a.enableButtons, enableButtons = _l === void 0 ? false : _l, error = _a.error, _m = _a.hideErrorMessage, hideErrorMessage = _m === void 0 ? false : _m, id = _a.id, _o = _a.incIcon, IncIcon = _o === void 0 ? PlusIcon.default : _o, increment = _a.increment, label = _a.label, _p = _a.maxValue, maxValue = _p === void 0 ? MAX_VALUE : _p, _q = _a.minValue, minValue = _q === void 0 ? MIN_VALUE : _q, onBlur = _a.onBlur, onChange = _a.onChange, onDecrement = _a.onDecrement, onError = _a.onError, onFocus = _a.onFocus, onIncrement = _a.onIncrement, onKeyDown = _a.onKeyDown, setError = _a.setError, setValue = _a.setValue, validator = _a.validator, _r = _a.validateOnChange, validateOnChange = _r === void 0 ? false : _r, _s = _a.value, value = _s === void 0 ? '' : _s, props = tslib.__rest(_a, ["allowInputOutOfBounds", "className", "decIcon", "decrement", "disabled", "enableButtons", "error", "hideErrorMessage", "id", "incIcon", "increment", "label", "maxValue", "minValue", "onBlur", "onChange", "onDecrement", "onError", "onFocus", "onIncrement", "onKeyDown", "setError", "setValue", "validator", "validateOnChange", "value"]);
    var t = reactI18next.useTranslation()[0];
    var _t = useDefaultInternalState.default(value, setValue), quantity = _t[0], setQuantity = _t[1];
    var _u = useDefaultInternalState.default(error, setError), errorState = _u[0], setErrorState = _u[1];
    var maxLength = maxValue.toString().length;
    var minimumValue = parseInt(minValue);
    var maximumValue = parseInt(maxValue);
    var validateQuantity = react.useCallback(function (currentValue) {
        var invalidQtyErrorMessage = t('SKULIST_INVALID_QUANTITY');
        var zeroOrEmptyQtyValidation = function (value) {
            return value < minimumValue || isNaN(value) || value > maximumValue
                ? invalidQtyErrorMessage
                : null;
        };
        var validate = new Validator.default(currentValue)
            .isValidQuantity(zeroOrEmptyQtyValidation, validator)
            .getError();
        setErrorState(validate);
    }, [setErrorState, t, validator]);
    /**
     * This useEffect allows for validation on pageLoad which is not handled by default via onBlur.
     */
    var isPageLoad = react.useRef(true);
    react.useEffect(function () {
        if (isPageLoad.current) {
            validateQuantity(parseInt(quantity));
            isPageLoad.current = false;
        }
    }, [quantity, validateQuantity]);
    var performIncrementCheck = function (value) {
        var retVal;
        var temp = parseInt(value !== null && value !== void 0 ? value : 1);
        var prevValue = Number.isNaN(temp) ? parseInt(minValue) : temp;
        var proposedValue = increment ? increment(prevValue) : prevValue + 1;
        if (proposedValue > maximumValue) {
            retVal = maximumValue;
        }
        else {
            retVal = proposedValue;
        }
        validateQuantity(retVal);
        return retVal.toString();
    };
    var incrementValue = function () {
        setErrorState(null);
        if (setValue) {
            var newVal = performIncrementCheck(value);
            onIncrement && onIncrement(value); //Temporary: Will remove it once removed from SSRO.
            setQuantity(newVal);
        }
        else {
            setQuantity(function (prev) { return performIncrementCheck(prev); });
        }
    };
    var performDecrementCheck = function (value) {
        var retVal;
        var temp = parseInt(value !== null && value !== void 0 ? value : 1);
        var prevValue = Number.isNaN(temp) ? parseInt(minValue) + 1 : temp;
        var proposedValue = decrement ? decrement(prevValue) : prevValue - 1;
        if (proposedValue < minimumValue) {
            retVal = minimumValue;
        }
        else {
            retVal = proposedValue;
        }
        validateQuantity(retVal);
        return retVal.toString();
    };
    var decrementValue = function () {
        setErrorState(null);
        if (setValue) {
            var newVal = performDecrementCheck(value);
            onDecrement && onDecrement(value); //Temporary: Will remove it once removed from SSRO.
            setQuantity(newVal);
        }
        else {
            setQuantity(function (prev) { return performDecrementCheck(prev); });
        }
    };
    var getInputValue = function (val) {
        var value = parseInt(val);
        return !isNaN(value) ? "".concat(val) : '';
    };
    var setInputValue = function (val) {
        var newValue = '';
        if (val) {
            if (parseInt(val) >= maximumValue && !allowInputOutOfBounds) {
                newValue = maxValue;
            }
            else {
                newValue = getInputValue(val);
            }
        }
        setQuantity(newValue);
    };
    var inputBlurHandler = function (e) {
        var _a;
        var strVal = e.target.value;
        var value = parseInt(strVal);
        validateQuantity(value);
        var qty = getInputValue(value.toString()); //To handle the condition for removing prefixed 0 values, for e.g 0001 to 1 on Input Blur.
        if (((_a = e === null || e === void 0 ? void 0 : e.target) === null || _a === void 0 ? void 0 : _a.value) !== qty) {
            setQuantity(qty);
        }
        if (onBlur) {
            onBlur(e);
        }
    };
    var inputFocusHandler = function (e) {
        setErrorState(null);
        if (onFocus) {
            onFocus(e);
        }
    };
    var inputOnChangeHandler = function (e) {
        if (validateOnChange) {
            var value_1 = parseInt(e.target.value);
            validateQuantity(value_1);
        }
        if (onChange) {
            onChange(e);
        }
    };
    // This is needed to prevent letter 'e'. It's technically allowed in the html input spec for type="number"
    // For IE we need to prevent all letters due to it doesn't comply with the specs
    var inputKeyDownHandler = function (e) {
        var _a, _b;
        var isNumber = /[0-9]/g.test(e.key); //regexp that test for numbers
        var isSpecialKey = e.key.length > 1; // e.key.length is always 1 expect for especial keys like 'Space', 'Enter', 'Backspace' etc..
        var isCopingOrPasting = (((_a = e.key) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'v' || ((_b = e.key) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === 'c') &&
            e.ctrlKey;
        if (!isNumber && !isSpecialKey && !isCopingOrPasting) {
            e.preventDefault();
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
    };
    var incProps = {
        disabled: false
    };
    var decProps = {
        disabled: false
    };
    if (disabled || parseInt(quantity) <= minimumValue) {
        decProps.disabled = true;
    }
    if (disabled || parseInt(quantity) >= maximumValue) {
        incProps.disabled = true;
    }
    return (jsxRuntime.jsxs("fieldset", tslib.__assign({ className: cx__default.default('d-flex', 'flex-column', 'p-1', className) }, { children: [label && (jsxRuntime.jsx("legend", tslib.__assign({ className: "mb-1 ".concat(alloyInput_module.default['alloy-legend']) }, { children: label }))), jsxRuntime.jsxs("div", tslib.__assign({ className: "d-inline-flex ".concat(alloyInput_module.default['quantity-input__container']) }, { children: [jsxRuntime.jsx("div", tslib.__assign({ className: "".concat(enableButtons ? "d-md-block" : 'd-md-none'), "data-testid": "quantity-minus-container" }, { children: jsxRuntime.jsx(AlloyButton.AlloyButton, tslib.__assign({ buttonType: "ghost", role: "button", className: cx__default.default(alloyInput_module.default['quantity-input__btn'], 'list-inline-item', 'd-flex justify-content-center align-items-center p-0 position-relative', (_b = {},
                                _b["".concat(alloyInput_module.default['btn-disabled'])] = disabled || parseInt(quantity) <= minimumValue,
                                _b), (_c = {},
                                _c["".concat(alloyInput_module.default['invalid'])] = errorState,
                                _c), alloyInput_module.default['quantity-input__border'], 'border-end-0', alloyInput_module.default['quantity-input__btn-minus']) }, decProps, { onClick: decrementValue }, { children: jsxRuntime.jsx(DecIcon, { className: "start-0 ".concat(alloyInput_module.default['quantity-input__indicator']), "aria-label": "Decrement", title: "Decrement" }) })) })), jsxRuntime.jsx("div", tslib.__assign({ className: "".concat(alloyInput_module.default['quantity-input__text-box'], " list-inline-item m-0") }, { children: jsxRuntime.jsx(Input.default, tslib.__assign({ "aria-label": label !== null && label !== void 0 ? label : null, className: cx__default.default(alloyInput_module.default['disable-spinner'], (_d = {},
                                _d["border-start-0 border-end-0"] = enableButtons,
                                _d["".concat(alloyInput_module.default['quantity-input__border'])] = !enableButtons,
                                _d["".concat(alloyInput_module.default['invalid'])] = errorState,
                                _d)), "data-testid": "quantity-input", id: id, value: getInputValue(quantity), setValue: setInputValue, onBlur: inputBlurHandler, onChange: inputOnChangeHandler, onFocus: inputFocusHandler, onKeyDown: inputKeyDownHandler, disabled: disabled, maxLength: maxLength, type: "number" }, props)) })), jsxRuntime.jsx("div", tslib.__assign({ className: "".concat(enableButtons ? "d-md-block" : 'd-md-none'), "data-testid": "quantity-plus-container" }, { children: jsxRuntime.jsx(AlloyButton.AlloyButton, tslib.__assign({ buttonType: "ghost", role: "button", className: cx__default.default(alloyInput_module.default['quantity-input__btn'], 'list-inline-item', 'd-flex justify-content-center align-items-center p-0 position-relative', (_e = {},
                                _e["".concat(alloyInput_module.default['btn-disabled'])] = disabled || parseInt(quantity) >= maximumValue,
                                _e), (_f = {},
                                _f["".concat(alloyInput_module.default['invalid'])] = errorState,
                                _f), alloyInput_module.default['quantity-input__border'], 'border-start-0', alloyInput_module.default['quantity-input__btn-plus']) }, incProps, { onClick: incrementValue }, { children: jsxRuntime.jsx(IncIcon, { className: "end-0 ".concat(alloyInput_module.default['quantity-input__indicator']), "aria-label": "Increment", title: "Increment" }) })) }))] })), !hideErrorMessage && !!errorState && (jsxRuntime.jsx("div", tslib.__assign({ className: cx__default.default('d-flex', 'quantity-input__error-msg', (_g = {},
                    _g["".concat(alloyInput_module.default['quantity-input__error-msg-no-btn'])] = !enableButtons,
                    _g["".concat(alloyInput_module.default['quantity-input__error-msg-with-btn'])] = enableButtons,
                    _g)) }, { children: jsxRuntime.jsx(ErrorMessage.default, tslib.__assign({ onShow: onError }, { children: errorState })) })))] })));
};
var QuantityInput$1 = QuantityInput;

exports.default = QuantityInput$1;
//# sourceMappingURL=QuantityInput.js.map
